import {
    type SaveAnnualServiceChargeStatementInput,
    createInvoiceFromBka,
    deleteAnnualServiceCharge,
    fetchAnnualServiceChargeStatementsByContract,
    fetchAnnualServiceChargeStatementsByEstate,
    fetchAnnualServiceChargeStatementsByTenancy,
    getAnnualServiceChargeStatement,
    saveAnnualServiceChargeStatement as saveAnnualServiceChargeStatementApi,
} from '@/api/property-management.api';
import type { AnnualServiceChargeStatement } from '@condo/domain';
import type { GetAnnualServiceChargeStatementsByTenancyQueryParams } from '@condo/sdk/propx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, computed, isRef, unref } from 'vue';

export const useGetServiceChargeStatementsByEstate = (estateId: MaybeRef<number | undefined>) => {
    const queryEnabled = computed(() => !!unref(estateId));

    return useQuery<AnnualServiceChargeStatement[]>(
        ['service-charge-statements', 'estate', estateId],
        async () => fetchAnnualServiceChargeStatementsByEstate(unref(estateId)!),
        {
            initialData: [],
            enabled: queryEnabled,
        },
    );
};

/*I am tired to write each time AnnualServiceChargeStatements, I am sorry let it be NBA - NebenkostenAbrechnung, please*/
export const useNBAStatements = (params: MaybeRef<GetAnnualServiceChargeStatementsByTenancyQueryParams>) => {
    const query = computed(() => unref(params));
    return useQuery(['service-charge-statements', query], async () => fetchAnnualServiceChargeStatementsByTenancy(unref(params)), {
        initialData: [],
    });
};

export const useNBAStatement = (serviceChargeStatementIdRef: MaybeRef<number | undefined>) => {
    const serviceChargeStatementId = computed(() => unref(serviceChargeStatementIdRef));
    const queryEnabled = computed(() => !!unref(serviceChargeStatementIdRef));

    return useQuery(
        ['service-charge-statements', unref(serviceChargeStatementId)],
        async () => {
            return getAnnualServiceChargeStatement(
                {
                    serviceChargeStatementId: unref(serviceChargeStatementId)!,
                },
                {
                    withDocuments: true,
                    withTransactions: true,
                },
            );
        },
        {
            // initialData: null as IAnnualServiceChargeStatement | null,
            enabled: queryEnabled, // Only enable query if ID is defined
        },
    );
};

export const useDeleteAnnualServiceChargeStatement = (serviceChargeStatementId: MaybeRef<number>, onSuccess?: () => void) => {
    return useMutation(async () => deleteAnnualServiceCharge(unref(serviceChargeStatementId)), { onSuccess });
};

export const useSaveAnnualServiceChargeStatement = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params: SaveAnnualServiceChargeStatementInput) => {
            return saveAnnualServiceChargeStatementApi(params);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['service-charge-statements', variables.serviceChargeStatementId] });
        },
    });
};

export const useBKAStatementsForOneRentalContract = (rentalContractId: MaybeRef<number>) => {
    return useQuery(
        ['service-charge-statements', 'rental-contract', rentalContractId],
        async () => {
            const rentalContractIdParam = isRef(rentalContractId) ? unref(rentalContractId) : rentalContractId;
            return fetchAnnualServiceChargeStatementsByContract({ rentalContractId: rentalContractIdParam });
        },
        { initialData: [], enabled: !!unref(rentalContractId) },
    );
};

export const useBKAStatementsForOneTenancy = (rentTenancyId: MaybeRef<number>) => {
    return useQuery(
        ['service-charge-statements', 'tenancy', rentTenancyId],
        async () => {
            const rentTenancyIdParam = isRef(rentTenancyId) ? unref(rentTenancyId) : rentTenancyId;
            // Note: This will need the tenancy to have the rentalContractId to work properly
            // For now, this is a placeholder that will need to be updated by consumers
            return fetchAnnualServiceChargeStatementsByTenancy({ rentTenancyId: rentTenancyIdParam });
        },
        { initialData: [], enabled: !!unref(rentTenancyId) },
    );
};

export const useCreateInvoiceFromStatement = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ serviceChargeStatementId, documentId }: { serviceChargeStatementId: MaybeRef<number>; documentId: MaybeRef<number> }) => {
            return createInvoiceFromBka(unref(serviceChargeStatementId), unref(documentId));
        },
        {
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries({ queryKey: ['service-charge-statements', variables.serviceChargeStatementId] });
            },
        },
    );
};
